/**
 * Servicio de gestión de dispositivos
 * Principios SOLID aplicados:
 * - SRP: Responsabilidad única de gestionar dispositivos
 * - DIP: Usa ApiService en lugar de fetch directo
 */

import { adminApi } from './ApiService';

class DeviceService {
  /**
   * Obtiene todos los dispositivos desde la API
   * @returns {Promise<Array>} Lista de dispositivos
   */
  async getAllDevices() {
    try {
      const response = await adminApi.get('/admin/firmware/versions');
      
      if (response.success) {
        return response.devices || [];
      } else {
        throw new Error(response.message || 'Error al cargar dispositivos');
      }
    } catch (error) {
      console.error('Error al cargar dispositivos:', error);
      throw error;
    }
  }

  /**
   * Obtiene información de un dispositivo específico
   * @param {string} mac - MAC address del dispositivo
   * @returns {Promise<Object>} Información del dispositivo
   */
  async getDeviceByMAC(mac) {
    try {
      const response = await adminApi.get(`/admin/devices/${mac}`);
      return response;
    } catch (error) {
      console.error(`Error al obtener dispositivo ${mac}:`, error);
      throw error;
    }
  }

  /**
   * Extrae la versión de firmware de un objeto de status complejo
   * @param {Object} device - Objeto del dispositivo
   * @param {Object} liveStatus - Estado en vivo del WebSocket (opcional)
   * @returns {string} Versión del firmware
   */
  extractFirmwareVersion(device, liveStatus = null) {
    // Lambdas now normalize to firmwareVersion — simple fallback chain
    return liveStatus?.firmwareVersion || device.firmwareVersion || device.currentFirmwareVersion || 'Desconocida';
  }

  /**
   * Combina información de BD con estado en vivo
   * @param {Object} device - Dispositivo de BD
   * @param {Map} activeDevices - Map de dispositivos activos
   * @returns {Object} Dispositivo con información combinada
   */
  mergeDeviceWithLiveStatus(device, activeDevices) {
    const activeStatus = activeDevices.get(device.mac);
    const firmwareVersion = this.extractFirmwareVersion(device, activeStatus);

    return {
      ...device,
      isActive: !!activeStatus,
      liveStatus: activeStatus || null,
      currentFirmwareVersion: firmwareVersion,
      lastSeen: activeStatus?.lastSeen || device.lastSeen || null,
    };
  }

  /**
   * Filtra dispositivos según criterios
   * @param {Array} devices - Lista de dispositivos
   * @param {Object} filters - Filtros a aplicar
   * @returns {Array} Dispositivos filtrados
   */
  filterDevices(devices, filters = {}) {
    let filtered = [...devices];

    // Filtrar por estado activo
    if (filters.isActive !== undefined) {
      filtered = filtered.filter(d => d.isActive === filters.isActive);
    }

    // Filtrar por modelo
    if (filters.model) {
      filtered = filtered.filter(d => d.model === filters.model);
    }

    // Filtrar por versión de firmware
    if (filters.firmwareVersion) {
      filtered = filtered.filter(
        d => d.currentFirmwareVersion === filters.firmwareVersion
      );
    }

    // Filtrar por búsqueda de texto
    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(
        d =>
          d.mac?.toLowerCase().includes(search) ||
          d.model?.toLowerCase().includes(search) ||
          d.name?.toLowerCase().includes(search)
      );
    }

    return filtered;
  }

  /**
   * Ordena dispositivos
   * @param {Array} devices - Lista de dispositivos
   * @param {string} field - Campo por el que ordenar
   * @param {string} direction - 'asc' o 'desc'
   * @returns {Array} Dispositivos ordenados
   */
  sortDevices(devices, field = 'mac', direction = 'asc') {
    return [...devices].sort((a, b) => {
      const aVal = a[field];
      const bVal = b[field];

      if (aVal === bVal) return 0;
      
      const comparison = aVal > bVal ? 1 : -1;
      return direction === 'asc' ? comparison : -comparison;
    });
  }
}

// Exportar como singleton
export default new DeviceService();
